/**
 * ExpertLegalRecords — shows the logged-in expert every per-booking
 * NDA / engagement / recording consent acceptance that a seeker signed
 * for one of their sessions.
 *
 * Each row is the expert's PROOF that the counterparty acknowledged
 * confidentiality for THIS session at THIS moment:
 *   - which booking
 *   - which seeker
 *   - which policy + version
 *   - timestamp (UTC)
 *   - sha256 of the exact text the seeker saw (auditable)
 *
 * Data lives in legal_acceptances WHERE booking_id IS NOT NULL.
 * Endpoint: GET /api/legal/expert/nda-history (returns sorted newest-first).
 *
 * Dropped into ExpertDashboardPage as a collapsible "Confidentiality
 * records" section.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, ExternalLink, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { API } from "@/App";


function formatHash(h) {
  if (!h) return "—";
  return `${h.slice(0, 12)}…${h.slice(-4)}`;
}


export default function ExpertLegalRecords({ defaultOpen = false }) {
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/legal/expert/nda-history`)
      .then((r) => { if (alive) setRecords(r.data.records || []); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e.message || "Failed to load records."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const count = records?.length || 0;

  return (
    <div className="bg-canvas-card border border-slateline-200 rounded-sm">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-brand-green" />
          <div>
            <h3 className="font-display text-lg text-ink">Confidentiality records</h3>
            <p className="text-xs text-slateline-500">
              {loading ? "Loading…" : `${count} signed acknowledgement${count === 1 ? "" : "s"} across your bookings`}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slateline-500" /> : <ChevronDown className="w-5 h-5 text-slateline-500" />}
      </button>

      {open && (
        <div className="border-t border-slateline-200 px-5 py-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slateline-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          {!loading && !error && count === 0 && (
            <p className="text-sm text-slateline-500 italic">
              No signed acknowledgements yet. They'll appear here as Seekers book and sign.
            </p>
          )}

          {!loading && !error && count > 0 && (
            <div className="space-y-2">
              {records.map((r) => (
                <div
                  key={r.acceptance_id}
                  className="border border-slateline-200 rounded-md p-3 bg-canvas"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-ink">
                          {r.seeker_name || r.seeker_email || "Seeker"}
                        </span>
                        <span className="text-xs text-slateline-400">·</span>
                        <span className="text-xs text-slateline-500">
                          {r.scheduled_date} {r.scheduled_time}
                        </span>
                        <span className="text-xs text-slateline-400">·</span>
                        <span className="text-xs text-slateline-500 capitalize">{r.booking_status}</span>
                      </div>
                      <div className="text-xs text-slateline-500 mt-1">
                        Signed{" "}
                        <span className="text-ink">
                          {new Date(r.signed_at).toLocaleString()}
                        </span>{" "}
                        ·{" "}
                        {r.policy_view_url ? (
                          <a
                            href={r.policy_view_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-brand-green underline hover:no-underline inline-flex items-center gap-0.5"
                          >
                            {r.policy_id} v{r.policy_version}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span>{r.policy_id} v{r.policy_version}</span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-slateline-400 mt-0.5">
                        hash {formatHash(r.text_hash)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
